import { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';

import { formatNumber } from '../../utils/generalFunctions';

import { contratarMembresia, cancelarMembresia, desactivarRenovacion } from '../../actions/membresia';
import { buscarMembresiasConfiguracion } from '../../actions/configuracion';

import { CustomValueContainer } from '../../components/react_select/ReactSelect';

const handleTarjetaIcono = (marca) => {
    switch(marca?.toLowerCase()){
        case 'visa':       return <i className="fa-brands fa-cc-visa fa-2x me-2"></i>;
        case 'mastercard': return <i className="fa-brands fa-cc-mastercard fa-2x me-2"></i>;
        case 'amex':       return <i className="fa-brands fa-cc-amex fa-2x me-2"></i>;
        default:           return <i className="fa-solid fa-credit-card fa-2x me-2"></i>;
    }
};

/* Formulario interno */
const FormContratacion = ({
    contratacionData,
    membresiasFilter,
    membresiaData,
    metodo_pago,
    onSelectMembresia,
    onContratar,
    onCancelar,
    onCerrar,
    onMejorar,
    onDesactivarRenovacion,
    statusProcesar
}) => {

    const styles_select = {
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        control: (provided) => ({ ...provided, paddingTop: '5px', paddingBottom: '5px' }),
        valueContainer: (provided) => ({ ...provided, overflow: 'visible', marginTop: '10px' }),
        placeholder: (provided, state) => ({
            ...provided,
            position: 'absolute',
            top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
            transition: 'top 0.1s, font-size 0.1s',
            fontSize: (state.hasValue || state.selectProps.inputValue) && 13
        })
    };

    /* Flujo Stripe: renovación activa → ofrecer desactivar o cancelar */
    if(contratacionData.status === 0 && contratacionData.stripe_price_id && !contratacionData.cancel_at_period_end){
        return (
            <Fragment>
                <Modal.Body>
                    <p className="mb-3">Gestiona tu membresía <strong>{contratacionData.nombre}</strong>.</p>
                    <div className="mb-3 p-3 border rounded">
                        <h6 className="fw-bold mb-1"><i className="fa-solid fa-ban me-2 text-warning"></i>Desactivar renovación</h6>
                        <p className="text-muted mb-2" style={{ fontSize: '0.875rem' }}>
                            Tu membresía no se renovará al final del periodo. Seguirás teniendo acceso a todos los beneficios hasta que venza.
                        </p>
                        {
                            statusProcesar
                                ? <button type="button" className="btn btn-warning btn-sm" disabled><span className="spinner-border spinner-border-sm me-1"/>Procesando...</button>
                                : <button type="button" className="btn btn-warning btn-sm" onClick={onDesactivarRenovacion}><i className="fa-solid fa-ban me-1"></i>Desactivar renovación</button>
                        }
                    </div>
                    <div className="p-3 border rounded">
                        <h6 className="fw-bold mb-1"><i className="fa-solid fa-circle-xmark me-2 text-danger"></i>Cancelar membresía</h6>
                        <p className="text-muted mb-2" style={{ fontSize: '0.875rem' }}>
                            Cancela tu membresía de forma inmediata. Perderás el acceso a los beneficios al final del periodo activo.
                        </p>
                        {
                            statusProcesar
                                ? <button type="button" className="btn btn-outline-danger btn-sm" disabled><span className="spinner-border spinner-border-sm me-1"/>Procesando...</button>
                                : <button type="button" className="btn btn-outline-danger btn-sm" onClick={onCancelar}><i className="fas fa-times fa-sm me-1"></i>Cancelar membresía</button>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={onCerrar} disabled={statusProcesar}>Volver</button>
                </Modal.Footer>
            </Fragment>
        );
    }

    /* Flujo Stripe: renovación ya desactivada */
    if(contratacionData.status === 0 && contratacionData.stripe_price_id && contratacionData.cancel_at_period_end){
        return (
            <Fragment>
                <Modal.Body>
                    <div className="alert alert-warning mb-3">
                        <i className="fa-solid fa-circle-info me-2"></i>
                        La renovación automática de <strong>{contratacionData.nombre}</strong> está desactivada. Tu acceso continuará hasta el final del periodo activo.
                    </div>
                    <p>Si lo deseas, también puedes cancelar la membresía de forma inmediata.</p>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={onCerrar} disabled={statusProcesar}>Cerrar</button>
                    {
                        statusProcesar
                            ? <button type="button" className="btn btn-danger" disabled><span className="spinner-border spinner-border-sm me-1"/>Cancelando...</button>
                            : <button type="button" className="btn btn-outline-danger" onClick={onCancelar}><i className="fas fa-times fa-sm me-1"></i> Cancelar ahora</button>
                    }
                </Modal.Footer>
            </Fragment>
        );
    }

    /* Flujo explorar (membresía activa) */
    if(contratacionData.status === 0 && contratacionData.total === 0){
        return (
            <Fragment>
                <Modal.Body>
                    <div className="text-center py-3">
                        <div className="mb-3" style={{ fontSize: '3rem' }}>🚀</div>
                        <h5 className="fw-bold mb-2">¡Estás en el plan gratuito!</h5>
                        <p className="text-muted mb-4">
                            Con la membresía <strong>{contratacionData.nombre}</strong> tienes acceso básico a la plataforma.
                            Mejora tu plan para desbloquear más asesorías, mayor seguimiento y beneficios exclusivos.
                        </p>
                        <button
                            type="button"
                            className="btn btn-success w-100 mb-2"
                            onClick={onMejorar}
                            disabled={statusProcesar}
                        >
                            <i className="fa-solid fa-arrow-trend-up me-2"></i> Ver planes disponibles
                        </button>
                        <button
                            type="button"
                            className="btn btn-link text-muted w-100"
                            style={{ fontSize: '0.85rem' }}
                            onClick={onCancelar}
                            disabled={statusProcesar}
                        >
                            {statusProcesar
                                ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Cancelando...</>
                                : 'Cancelar membresía'
                            }
                        </button>
                    </div>
                </Modal.Body>
            </Fragment>
        );
    }

    /* Flujo cancelar (membresía de pago activa) */
    if(contratacionData.status === 0){
        return (
            <Fragment>
                <Modal.Body>
                    <p>
                        ¿Deseas cancelar tu membresía <strong>{contratacionData.nombre}</strong>?
                        Una vez cancelada perderás el acceso a los beneficios al final del periodo activo.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onCerrar}
                        disabled={statusProcesar}
                    >
                        No, conservar
                    </button>
                    {
                        statusProcesar ?
                            <button type="button" className="btn btn-danger" disabled>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Cancelando...
                            </button>
                        :
                            <button type="button" className="btn btn-danger" onClick={onCancelar}>
                                <i className="fas fa-times fa-sm me-1"></i> Sí, cancelar
                            </button>
                    }
                </Modal.Footer>
            </Fragment>
        );
    }

    /* Flujo contratar */
    const createFilasBeneficios = () => {
        if(!membresiaData?.beneficios?.length) {
            return <li><span>Sin beneficios disponibles.</span></li>;
        }
        return membresiaData.beneficios.map((b, i) => (
            <li key={i}><span>{b}</span></li>
        ));
    };

    return (
        <Fragment>
            <Modal.Body>
                <p>Selecciona el plan que deseas contratar.</p>

                <div className="row">
                    <div className="col-md-8 form-group mb-3">
                        <div className="input-group h-100">
                            <Select
                                id="id_membresia"
                                name="id_membresia"
                                className="select-group"
                                classNamePrefix="react-select"
                                placeholder="Membresía"
                                isClearable={true}
                                options={membresiasFilter}
                                value={membresiaData}
                                onChange={onSelectMembresia}
                                menuPortalTarget={document.body}
                                components={{ ValueContainer: CustomValueContainer }}
                                styles={{ ...styles_select }}
                            />
                        </div>
                    </div>

                    <div className="col-md-4 form-group mb-3">
                        <label className="fw-bold">Cobro anual</label>
                        <p>{contratacionData.total != null ? (contratacionData.total === 0 ? 'Gratuita' : formatNumber(contratacionData.total, 2, '$ ', '') + ' IVA inc.') : 'Sin definir'}</p>
                    </div>
                </div>

                {
                    membresiaData?.beneficios?.length > 0 &&
                    <div className="row mb-3">
                        <div className="col-md-12 form-group">
                            <label className="fw-bold mb-2">Beneficios</label>
                            <ul className="features-list">{ createFilasBeneficios() }</ul>
                        </div>
                    </div>
                }

                {/* Método de pago — solo para membresías con costo */}
                {
                    contratacionData.total !== 0 &&
                    <div className="row">
                        <div className="col-md-12">
                            <label className="fw-bold mb-2">Método de pago</label>
                            {
                                metodo_pago?.digitos ?
                                    <div className="d-flex align-items-center p-3 border rounded bg-light">
                                        { handleTarjetaIcono(metodo_pago.marca) }
                                        <div>
                                            <p className="mb-0 fw-bold">•••• •••• •••• {metodo_pago.digitos}</p>
                                            <small className="text-muted">Vence {metodo_pago.expiracion_mes}/{metodo_pago.expiracion_ano}</small>
                                        </div>
                                    </div>
                                :
                                    <div className="alert alert-warning mb-0" role="alert">
                                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                        No tienes una tarjeta registrada. Agrega una antes de contratar.
                                        <NavLink
                                            to="/configuracion/metodo_pago"
                                            className="btn btn-warning btn-sm ms-3"
                                        >
                                            <i className="fa-solid fa-credit-card me-1"></i> Agregar tarjeta
                                        </NavLink>
                                    </div>
                            }
                        </div>
                    </div>
                }
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-danger"
                    onClick={onCerrar}
                    disabled={statusProcesar}
                >
                    <i className="fas fa-times fa-sm"></i> Cancelar
                </button>
                {
                    statusProcesar ?
                        <button type="button" className="btn btn-success" disabled>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Procesando...
                        </button>
                    :
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={onContratar}
                            disabled={!contratacionData.id_membresia || (contratacionData.total !== 0 && !metodo_pago?.digitos)}
                        >
                            <i className="fas fa-check fa-sm me-1"></i> Contratar
                        </button>
                }
            </Modal.Footer>
        </Fragment>
    );
};

/* Componente principal */
const GestionarMembresia = ({ membresia, reestablecerMembresia }) => {

    const dispatch = useDispatch();

    const { listado } = useSelector(state => state.configuracion.membresias);
    const { metodo_pago } = useSelector(state => state.configuracion);

    const [contratacionData, setContratacionData] = useState({
        id_contratacion: null,
        id_membresia: null,
        nombre: null,
        status: null,
        total: null,
        cancel_at_period_end: false,
        stripe_price_id: null
    });

    const [flujoMejorar, setFlujoMejorar] = useState(false);

    const [membresiaData, setMembresiaData] = useState(null);
    const [membresiasFilter, setMembresiasFilter] = useState([]);
    const [membresiaModal, setMembresiaModal] = useState(false);
    const [statusProcesar, setStatusProcesar] = useState(false);

    /* Handlers */
    const handleAbrir = () => {
        dispatch(buscarMembresiasConfiguracion());
    };

    const handleSelectMembresia = (option) => {
        setMembresiaData(option);
        setContratacionData({
            ...contratacionData,
            id_membresia: option?.value ?? null,
            total: option?.precio ?? null
        });
    };

    const handleContratar = async () => {
        setStatusProcesar(true);

        await dispatch(contratarMembresia({
            id_membresia: contratacionData.id_membresia
        }));

        setStatusProcesar(false);
        handleCerrar();
    };

    const handleCancelar = async () => {
        setStatusProcesar(true);

        await dispatch(cancelarMembresia({
            id_contratacion: contratacionData.id_contratacion
        }));

        setStatusProcesar(false);
        handleCerrar();
    };

    const handleDesactivarRenovacion = async () => {
        setStatusProcesar(true);

        await dispatch(desactivarRenovacion({
            id_contratacion: contratacionData.id_contratacion
        }));

        setStatusProcesar(false);
        handleCerrar();
    };

    const handleMejorar = () => {
        setFlujoMejorar(true);
        setContratacionData(prev => ({ ...prev, status: null }));
    };

    const handleCerrar = () => {
        if(statusProcesar) return;

        setContratacionData({
            id_contratacion: null,
            id_membresia: null,
            nombre: null,
            status: null,
            total: null,
            cancel_at_period_end: false,
            stripe_price_id: null
        });
        setMembresiaData(null);
        setFlujoMejorar(false);
        reestablecerMembresia(null);
        setMembresiaModal(false);
    };

    /* Effects */
    useEffect(() => {
        if(membresia) {
            setFlujoMejorar(false);
            setContratacionData({
                ...contratacionData,
                id_contratacion: membresia._id,
                id_membresia: membresia.id_membresia,
                nombre: membresia.nombre,
                status: membresia.status,
                total: membresia.total ?? null,
                cancel_at_period_end: membresia.cancel_at_period_end ?? false,
                stripe_price_id: membresia.stripe_price_id ?? null
            });
            setMembresiaModal(true);
        }
    }, [membresia]);

    useEffect(() => {
        const options = listado
            .filter(m => contratacionData.status !== 0 || String(m._id) !== String(contratacionData.id_membresia))
            .map(m => ({
                value: m._id,
                label: m.nombre,
                precio: m.precio,
                beneficios: m.beneficios,
                stripe_price_id: m.stripe_price_id
            }));
        setMembresiasFilter(options);
    }, [listado, contratacionData.id_membresia]);

    return (
        <Modal
            size="md"
            show={membresiaModal}
            backdrop="static"
            centered
            onEntered={handleAbrir}
            onHide={handleCerrar}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {contratacionData.status === 0 && contratacionData.total === 0
                        ? '¡Mejora tu plan!'
                        : contratacionData.status === 0 && contratacionData.stripe_price_id
                            ? 'Administrar membresía'
                            : contratacionData.status === 0
                                ? 'Cancelar membresía'
                                : flujoMejorar ? 'Mejora tu plan' : 'Contratar membresía'
                    }
                </Modal.Title>
            </Modal.Header>

            <FormContratacion
                contratacionData={contratacionData}
                membresiasFilter={membresiasFilter}
                membresiaData={membresiaData}
                metodo_pago={metodo_pago}
                onSelectMembresia={handleSelectMembresia}
                onContratar={handleContratar}
                onCancelar={handleCancelar}
                onCerrar={handleCerrar}
                onMejorar={handleMejorar}
                onDesactivarRenovacion={handleDesactivarRenovacion}
                statusProcesar={statusProcesar}
            />
        </Modal>
    );
};

export default GestionarMembresia;
